import { Children, type ReactElement, type ReactNode } from 'react';
import { isElement } from 'react-is';

import { Divider } from '@dynatrace/strato-components/layouts';

import { isSlot } from '../../../core/utils/is-slot.js';
import { Menu } from '../../../navigation/menu/Menu.js';
import { Group } from '../components/series-action/components/Group.js';
import { Intent } from '../components/series-action/components/Intent.js';
import { Item } from '../components/series-action/components/Item.js';
import { ItemIcon } from '../components/series-action/components/ItemIcon.js';
import { ItemTooltip } from '../components/series-action/components/ItemTooltip.js';
import { Label } from '../components/series-action/components/Label.js';
import { Link } from '../components/series-action/components/Link.js';
import { Sub } from '../components/series-action/components/Sub.js';
import { SubContent } from '../components/series-action/components/SubContent.js';
import { SubTrigger } from '../components/series-action/components/SubTrigger.js';

const sortResults = (
  seriesActions: (ReactElement | undefined)[],
  intents: (ReactElement | undefined)[],
) => {
  if (seriesActions.length === 0) {
    return intents;
  }
  if (intents.length === 0) {
    return seriesActions;
  }

  return [...seriesActions, <Divider key="divider" />, ...intents];
};

/**
 * Wraps the passed child element in the corresponding Menu component
 * or calls the function recursively if the child is a Group
 *
 * @param children - element to render
 */
export function renderMenuActionChildren(children: ReactNode) {
  if (!children) {
    return;
  }

  const seriesActions: (ReactElement | undefined)[] = [];
  const intents: (ReactElement | undefined)[] = [];
  Children.forEach(children, (child) => {
    if (isSlot(child, Sub)) {
      seriesActions.push(
        <Menu.Sub>
          {Children.map(child?.props?.children, (itemChild) => {
            if (isElement(itemChild)) {
              return renderMenuActionChildren(itemChild);
            }
          })}
        </Menu.Sub>,
      );
    }

    if (isSlot(child, SubTrigger)) {
      seriesActions.push(
        <Menu.SubTrigger>
          {Children.map(child?.props?.children, (itemChild) => {
            if (isSlot(itemChild, ItemTooltip)) {
              return <Menu.ItemTooltip>{itemChild}</Menu.ItemTooltip>;
            } else if (isSlot(itemChild, ItemIcon)) {
              return <Menu.ItemIcon>{itemChild}</Menu.ItemIcon>;
            }
            return itemChild;
          })}
        </Menu.SubTrigger>,
      );
    }

    if (isSlot(child, SubContent)) {
      seriesActions.push(
        <Menu.SubContent>
          {Children.map(child?.props?.children, (itemChild) => {
            if (isElement(itemChild)) {
              return renderMenuActionChildren(itemChild);
            }
          })}
        </Menu.SubContent>,
      );
    }

    if (isSlot(child, Item)) {
      seriesActions.push(
        <Menu.Item
          key={child.key}
          onSelect={child.props.onSelect}
          disabled={child.props.disabled}
        >
          {Children.map(child?.props?.children, (itemChild) => {
            if (isSlot(itemChild, ItemTooltip)) {
              return <Menu.ItemTooltip>{itemChild}</Menu.ItemTooltip>;
            } else if (isSlot(itemChild, ItemIcon)) {
              return <Menu.ItemIcon>{itemChild}</Menu.ItemIcon>;
            }

            return itemChild;
          })}
        </Menu.Item>,
      );
    }

    if (isSlot(child, Label)) {
      seriesActions.push(<Menu.Label>{child}</Menu.Label>);
    }

    if (isSlot(child, Group)) {
      seriesActions.push(
        <Menu.Group>
          {Children.map(child?.props?.children, (itemChild) => {
            if (isElement(itemChild)) {
              return renderMenuActionChildren(itemChild);
            }
          })}
        </Menu.Group>,
      );
    }

    if (isSlot(child, Link)) {
      seriesActions.push(
        <Menu.Link key={child.key} data-link={true} {...child.props}>
          {child}
        </Menu.Link>,
      );
    }

    if (isSlot(child, Intent)) {
      intents.push(
        <Menu.Intent key={child.key} {...child.props}>
          {child}
        </Menu.Intent>,
      );
    }
  });

  return intents.length > 0 || seriesActions.length > 0
    ? sortResults(seriesActions, intents)
    : undefined;
}
